import React, { useEffect, useState } from "react";
import { api, API } from "../lib/api";
import Panel from "../components/Panel";
import { toast } from "sonner";
import {
    Settings as SettingsIcon, Building2, Sparkles, FileSpreadsheet,
    Download, Upload, RotateCcw, Save, AlertTriangle, Check, X, ShieldCheck, Filter, RefreshCw,
    FileText, FileType, Table2, ExternalLink, Link as LinkIcon, Sun, Moon, Palette,
} from "lucide-react";
import { Link } from "react-router-dom";
import { useTheme } from "../lib/theme";

// ============ Fresh Start Modal (destructive — extra guard) ============
function FreshStartModal({ open, onClose, onDone }) {
    const [confirm, setConfirm] = useState("");
    const [busy, setBusy] = useState(false);
    const [fyStart, setFyStart] = useState("2026-04-01");
    const [fyEnd, setFyEnd] = useState("2027-03-31");
    const [fyLabel, setFyLabel] = useState("FY 2026-27");
    const [preserveTemplates, setPreserveTemplates] = useState(true);

    useEffect(() => { if (open) { setConfirm(""); } }, [open]);
    if (!open) return null;

    const canRun = confirm === "WIPE" && !busy;

    const run = async () => {
        setBusy(true);
        try {
            const r = await api.post("/settings/fresh-start", {
                fy_start: fyStart, fy_end: fyEnd, fy_label: fyLabel,
                preserve_templates: preserveTemplates,
            });
            toast.success(`✅ ${r.data.message}`);
            onDone?.(r.data);
            onClose();
        } catch (e) {
            toast.error(e.response?.data?.detail || "Fresh Start failed");
        } finally { setBusy(false); }
    };

    return (
        <div className="fixed inset-0 bg-[var(--bg-base)]/80 z-50 flex items-center justify-center p-4" onClick={onClose}>
            <div className="bg-panel border-2 border-[var(--neg)]/60 max-w-lg w-full" onClick={(e) => e.stopPropagation()}>
                <div className="border-b border-panel p-4 flex items-center justify-between">
                    <div className="flex items-center gap-2 text-[var(--neg)]">
                        <AlertTriangle size={18} />
                        <div className="font-display font-black text-lg">Fresh Start · Destructive Operation</div>
                    </div>
                    <button onClick={onClose} className="text-[var(--text-secondary)] hover:text-[var(--text-primary)]"><X size={16} /></button>
                </div>
                <div className="p-5 space-y-4">
                    <div className="text-sm font-body text-[var(--text-primary)]/90 space-y-2">
                        <div>This will <b className="text-[var(--neg)]">permanently wipe</b>:</div>
                        <ul className="list-disc list-inside text-xs text-[var(--text-primary)]/80 space-y-0.5 ml-2">
                            <li>All 14 data entities (projects, vendors, employees, inventory, bank, etc.)</li>
                            <li>All vouchers, POs, GRNs, sales orders, payroll runs, bank statements</li>
                            <li>All trial-balance ledgers + monthly series + cashflow forecast</li>
                        </ul>
                        <div className="pt-2 text-[var(--pos)]">✓ Preserved: your team logins, {preserveTemplates ? "chart of accounts + statutory compliance calendar" : "nothing else"}</div>
                    </div>

                    <div className="grid grid-cols-2 gap-2">
                        <div>
                            <div className="text-[10px] font-mono-fin uppercase tracking-wider text-[var(--text-secondary)] mb-1">FY Start</div>
                            <input data-testid="fs-fy-start" value={fyStart} onChange={(e) => setFyStart(e.target.value)}
                                className="w-full bg-[var(--bg-base)] border border-panel px-3 py-1.5 text-xs font-mono-fin" />
                        </div>
                        <div>
                            <div className="text-[10px] font-mono-fin uppercase tracking-wider text-[var(--text-secondary)] mb-1">FY End</div>
                            <input data-testid="fs-fy-end" value={fyEnd} onChange={(e) => setFyEnd(e.target.value)}
                                className="w-full bg-[var(--bg-base)] border border-panel px-3 py-1.5 text-xs font-mono-fin" />
                        </div>
                        <div className="col-span-2">
                            <div className="text-[10px] font-mono-fin uppercase tracking-wider text-[var(--text-secondary)] mb-1">FY Label</div>
                            <input data-testid="fs-fy-label" value={fyLabel} onChange={(e) => setFyLabel(e.target.value)}
                                className="w-full bg-[var(--bg-base)] border border-panel px-3 py-1.5 text-xs font-mono-fin" />
                        </div>
                    </div>

                    <label className="flex items-center gap-2 text-xs font-body text-[var(--text-primary)]/85">
                        <input type="checkbox" checked={preserveTemplates} onChange={(e) => setPreserveTemplates(e.target.checked)}
                            className="accent-[var(--brand-amber)]" data-testid="fs-preserve" />
                        Preserve compliance calendar (recommended)
                    </label>

                    <div>
                        <div className="text-[10px] font-mono-fin uppercase tracking-wider text-[var(--neg)] mb-1">
                            Type <b>WIPE</b> below to enable the button
                        </div>
                        <input
                            data-testid="fs-confirm-input"
                            value={confirm}
                            onChange={(e) => setConfirm(e.target.value)}
                            placeholder="Type WIPE"
                            className="w-full bg-[var(--bg-base)] border border-[var(--neg)]/40 px-3 py-2 text-sm font-mono-fin text-[var(--neg)] focus:outline-none focus:border-[var(--neg)]"
                        />
                    </div>

                    <button
                        data-testid="fs-run"
                        onClick={run}
                        disabled={!canRun}
                        className={`w-full px-4 py-3 text-xs font-mono-fin uppercase tracking-wider flex items-center justify-center gap-2 transition-colors ${canRun ? "bg-[var(--neg)] text-[var(--text-primary)] hover:bg-red-700" : "bg-panel text-[var(--text-secondary)] cursor-not-allowed"}`}
                    >
                        <RotateCcw size={12} /> {busy ? "Wiping…" : "I understand — Fresh Start Now"}
                    </button>
                </div>
            </div>
        </div>
    );
}

// ============ Universal Import Hub (Excel · CSV · Any File · Google Sheets guide) ============
function ImportHubPanel() {
    const [file, setFile] = useState(null);
    const [busy, setBusy] = useState(false);
    const [result, setResult] = useState(null);

    // Any-file uploader state
    const [anyFile, setAnyFile] = useState(null);
    const [anyBusy, setAnyBusy] = useState(false);
    const [anyNote, setAnyNote] = useState("");
    const [anyResult, setAnyResult] = useState(null);

    const downloadTemplate = () => {
        window.open(`${API}/settings/template.xlsx`, "_blank");
    };

    const uploadExcel = async () => {
        if (!file) { toast.error("Pick an .xlsx file"); return; }
        setBusy(true); setResult(null);
        const fd = new FormData();
        fd.append("file", file);
        try {
            const r = await fetch(`${API}/settings/import-excel`, { method: "POST", body: fd });
            const data = await r.json();
            if (!r.ok) throw new Error(data.detail || "Import failed");
            setResult(data);
            toast.success(`Imported ${Object.keys(data.sheets_imported).length} sheet(s)`);
        } catch (e) {
            toast.error(e.message);
        } finally { setBusy(false); }
    };

    const uploadAnyFile = async () => {
        if (!anyFile) { toast.error("Pick a file to upload"); return; }
        setAnyBusy(true); setAnyResult(null);
        const fd = new FormData();
        fd.append("file", anyFile);
        fd.append("entity_type", "generic");
        fd.append("entity_id", "");
        fd.append("note", anyNote || "");
        try {
            const r = await fetch(`${API}/files/upload`, { method: "POST", body: fd });
            const data = await r.json();
            if (!r.ok) throw new Error(data.detail || "Upload failed");
            setAnyResult(data);
            toast.success(`Uploaded ${anyFile.name} (${(anyFile.size / 1024).toFixed(1)} KB)`);
            setAnyFile(null); setAnyNote("");
        } catch (e) {
            toast.error(e.message);
        } finally { setAnyBusy(false); }
    };

    return (
        <div className="space-y-3">
            <Panel title="1 · Master Excel Import" subtitle="One .xlsx file → all 14 entities + Company profile. Overwrites per-sheet.">
                <div className="space-y-4">
                    <div>
                        <button
                            data-testid="template-download"
                            onClick={downloadTemplate}
                            className="border border-panel px-4 py-2 text-xs font-mono-fin uppercase tracking-wider hover:border-[var(--brand-amber)] hover:text-[var(--brand-amber)] transition-colors flex items-center gap-2"
                        >
                            <Download size={12} /> Download Blank Template (14 sheets + Company)
                        </button>
                        <div className="text-[10px] font-mono-fin text-[var(--text-muted)] mt-2">Sheet names & column headers pre-populated with one sample row each. Delete the sample row before uploading real data.</div>
                    </div>

                    <div className="border-t border-panel pt-4">
                        <div className="text-[10px] font-mono-fin uppercase tracking-wider text-[var(--text-secondary)] mb-2">Upload filled .xlsx</div>
                        <div className="flex items-center gap-2">
                            <input
                                type="file"
                                accept=".xlsx"
                                data-testid="excel-file"
                                onChange={(e) => setFile(e.target.files[0])}
                                className="flex-1 bg-[var(--bg-base)] border border-panel px-3 py-2 text-xs font-mono-fin"
                            />
                            <button
                                data-testid="excel-upload"
                                onClick={uploadExcel}
                                disabled={busy || !file}
                                className="bg-[var(--brand-amber)] text-black px-4 py-2 text-xs font-mono-fin uppercase tracking-wider hover:bg-[var(--bg-panel-hover)] transition-colors disabled:opacity-50 flex items-center gap-2"
                            >
                                <Upload size={12} /> {busy ? "Importing…" : "Import"}
                            </button>
                        </div>
                        {file && <div className="text-[10px] font-mono-fin uppercase tracking-wider text-[var(--pos)] mt-2">✓ {file.name}</div>}
                    </div>

                    {result && (
                        <div className="border border-[var(--pos)]/40 bg-[var(--pos)]/5 p-3">
                            <div className="text-[10px] font-mono-fin uppercase tracking-wider text-[var(--pos)] mb-2 flex items-center gap-1"><Check size={12} /> Import Complete</div>
                            <table className="w-full text-xs font-body">
                                <tbody>
                                    {Object.entries(result.sheets_imported).map(([k, n]) => (
                                        <tr key={k} className="border-b border-panel/40"><td className="py-1">{k}</td><td className="text-right py-1 font-mono-fin text-[var(--pos)]">{n} row(s)</td></tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}
                </div>
            </Panel>

            <Panel title="2 · Per-Entity CSV Import" subtitle="Add/replace rows in a single entity (projects, vendors, employees, …)">
                <div className="text-xs font-body text-[var(--text-primary)]/85 space-y-3">
                    <p>Each of the 14 entities in <span className="text-[var(--brand-amber)] font-mono-fin">Data Manager</span> has its own CSV import button. Steps:</p>
                    <ol className="list-decimal list-inside space-y-1 ml-2 text-[var(--text-primary)]/80">
                        <li>Open <Link to="/data" className="text-[var(--brand-amber)] hover:underline inline-flex items-center gap-1">Data Manager <ExternalLink size={11} /></Link></li>
                        <li>Pick an entity from the left rail (e.g. Projects, Vendors, Clients).</li>
                        <li>Click <span className="font-mono-fin bg-[var(--bg-base)] px-1.5 border border-panel">CSV Import</span> in the top bar → pick your file.</li>
                        <li>First row must be the column headers matching the field names shown in the table.</li>
                    </ol>
                    <div className="pt-2 border-t border-panel">
                        <Link to="/data" data-testid="link-data-manager" className="inline-flex items-center gap-2 bg-panel border border-panel px-3 py-1.5 text-[11px] font-mono-fin uppercase tracking-wider hover:border-[var(--brand-amber)] hover:text-[var(--brand-amber)] transition-colors">
                            <Table2 size={12} /> Open Data Manager
                        </Link>
                    </div>
                </div>
            </Panel>

            <Panel title="3 · Any File Upload · PDF · Word · Images · ZIP" subtitle="Attach receipts, contracts, GST notices, statements — any file up to 25 MB">
                <div className="space-y-3">
                    <div className="text-xs font-body text-[var(--text-primary)]/85">
                        Supported: <span className="font-mono-fin text-[var(--pos)]">PDF · DOC/DOCX · XLSX (as attachment) · CSV · TXT · PNG · JPG · WebP · ZIP</span>. Stored in the Document Vault and searchable there.
                    </div>
                    <div>
                        <input
                            type="file"
                            data-testid="any-file-input"
                            onChange={(e) => setAnyFile(e.target.files[0])}
                            className="w-full bg-[var(--bg-base)] border border-panel px-3 py-2 text-xs font-mono-fin"
                        />
                    </div>
                    <input
                        type="text"
                        placeholder="Note (optional) — e.g. GST-July-return.pdf received from consultant"
                        value={anyNote}
                        onChange={(e) => setAnyNote(e.target.value)}
                        data-testid="any-file-note"
                        className="w-full bg-[var(--bg-base)] border border-panel px-3 py-2 text-xs font-body"
                    />
                    <div className="flex items-center gap-2">
                        <button
                            data-testid="any-file-upload"
                            onClick={uploadAnyFile}
                            disabled={anyBusy || !anyFile}
                            className="bg-[var(--brand-amber)] text-black px-4 py-2 text-xs font-mono-fin uppercase tracking-wider hover:bg-[var(--bg-panel-hover)] transition-colors disabled:opacity-50 flex items-center gap-2"
                        >
                            <Upload size={12} /> {anyBusy ? "Uploading…" : "Upload File"}
                        </button>
                        <Link to="/vault" data-testid="link-vault" className="border border-panel px-3 py-2 text-[11px] font-mono-fin uppercase tracking-wider hover:border-[var(--brand-amber)] hover:text-[var(--brand-amber)] transition-colors flex items-center gap-2">
                            <FileText size={12} /> Open Document Vault
                        </Link>
                    </div>
                    {anyResult && (
                        <div className="border border-[var(--pos)]/40 bg-[var(--pos)]/5 p-3 text-xs font-mono-fin text-[var(--pos)] flex items-center gap-2">
                            <Check size={14} /> Uploaded · {anyResult.filename} · id {anyResult.id}
                        </div>
                    )}
                </div>
            </Panel>

            <Panel title="4 · Google Sheets" subtitle="No direct sync — export → upload (30 seconds)">
                <div className="text-xs font-body text-[var(--text-primary)]/85 space-y-2">
                    <p>Google doesn&apos;t allow direct read without OAuth setup. Simplest path:</p>
                    <ol className="list-decimal list-inside space-y-1 ml-2 text-[var(--text-primary)]/80">
                        <li>Open your Google Sheet.</li>
                        <li>File → Download → <b>Microsoft Excel (.xlsx)</b> or <b>Comma-separated values (.csv)</b>.</li>
                        <li>Upload via Section 1 (Master Excel) or Section 2 (Per-Entity CSV) above.</li>
                    </ol>
                    <p className="pt-2 text-[10px] font-mono-fin uppercase tracking-wider text-[var(--text-muted)]">If you need real-time bi-directional sync, tell me and I&apos;ll wire in Google Sheets API + OAuth (~30 min).</p>
                </div>
            </Panel>

            <div className="border border-panel bg-panel/40 p-3 text-[10px] font-mono-fin uppercase tracking-wider text-[var(--text-muted)] flex items-start gap-2">
                <LinkIcon size={12} className="text-[var(--brand-amber)] mt-0.5" />
                <div>Every upload above writes to MongoDB and triggers all 10 AI agents to recompute their digests in the background. Every action lands in the <b className="text-[var(--brand-amber)]">Audit Trail</b> tab with your email, timestamp, and before/after payload.</div>
            </div>
        </div>
    );
}

// ============ Company Profile Panel ============
function CompanyPanel() {
    const [company, setCompany] = useState({});
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [dirty, setDirty] = useState({});

    const load = async () => {
        setLoading(true);
        try {
            const r = await api.get("/settings/company");
            setCompany(r.data || {});
        } finally { setLoading(false); }
    };
    useEffect(() => { load(); }, []);

    const patch = (k, v) => { setDirty({ ...dirty, [k]: v }); };
    const value = (k) => dirty[k] !== undefined ? dirty[k] : company[k];

    const save = async () => {
        setSaving(true);
        try {
            await api.put("/settings/company", dirty);
            toast.success("Company profile updated");
            setDirty({});
            load();
        } catch (e) {
            toast.error(e.response?.data?.detail || "Save failed");
        } finally { setSaving(false); }
    };

    const resetProfile = async () => {
        if (!window.confirm("Reset company profile to original values?")) return;
        try {
            await api.post("/settings/reset-company");
            toast.success("Profile reset");
            setDirty({}); load();
        } catch (e) {
            toast.error("Reset failed");
        }
    };

    const fields = [
        ["name", "Company Name"],
        ["cin", "CIN"],
        ["gstin", "GSTIN"],
        ["pan", "PAN"],
        ["hq", "Registered Address (HQ)"],
        ["fy", "Financial Year Label"],
        ["period_start", "FY Start (YYYY-MM-DD)"],
        ["period_end", "FY End (YYYY-MM-DD)"],
        ["turnover_target_annual", "Annual Turnover Target (₹)"],
        ["turnover_target_18m", "18-Month Target (₹)"],
        ["tagline", "Tagline (optional)"],
    ];

    return (
        <Panel title="Company Profile" subtitle="Legal & display information across every report">
            {loading ? (
                <div className="text-xs font-mono-fin text-[var(--text-secondary)] p-4">Loading…</div>
            ) : (
                <div className="space-y-3">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                        {fields.map(([f, label]) => (
                            <div key={f}>
                                <div className="text-[10px] font-mono-fin uppercase tracking-wider text-[var(--text-secondary)] mb-1">{label}</div>
                                <input
                                    data-testid={`company-${f}`}
                                    value={value(f) ?? ""}
                                    onChange={(e) => patch(f, e.target.value)}
                                    className="w-full bg-[var(--bg-base)] border border-panel px-3 py-1.5 text-xs font-mono-fin focus:border-[var(--brand-amber)] focus:outline-none"
                                />
                            </div>
                        ))}
                    </div>

                    <div className="flex gap-2 pt-3 border-t border-panel">
                        <button
                            data-testid="company-save"
                            onClick={save}
                            disabled={saving || Object.keys(dirty).length === 0}
                            className="bg-[var(--brand-amber)] text-black px-4 py-2 text-xs font-mono-fin uppercase tracking-wider hover:bg-[var(--bg-panel-hover)] transition-colors disabled:opacity-50 flex items-center gap-2"
                        >
                            <Save size={12} /> {saving ? "Saving…" : "Save Profile"}
                        </button>
                        <button
                            data-testid="company-reset"
                            onClick={resetProfile}
                            className="border border-[var(--neg)]/40 text-[var(--neg)] px-4 py-2 text-xs font-mono-fin uppercase tracking-wider hover:bg-[var(--neg)]/10 transition-colors flex items-center gap-2"
                        >
                            <RotateCcw size={12} /> Reset
                        </button>
                        {Object.keys(dirty).length > 0 && (
                            <div className="text-[10px] font-mono-fin uppercase tracking-wider text-[var(--brand-amber)] self-center">
                                {Object.keys(dirty).length} unsaved change{Object.keys(dirty).length > 1 ? "s" : ""}
                            </div>
                        )}
                    </div>
                </div>
            )}
        </Panel>
    );
}

// ============ Audit Trail Panel ============
function AuditTrailPanel() {
    const [entries, setEntries] = useState([]);
    const [summary, setSummary] = useState(null);
    const [loading, setLoading] = useState(true);
    const [filters, setFilters] = useState({ entity: "", actor: "", action: "" });
    const [expanded, setExpanded] = useState(null);

    const load = async (filtersArg = filters) => {
        setLoading(true);
        try {
            const params = {};
            if (filtersArg.entity) params.entity = filtersArg.entity;
            if (filtersArg.actor) params.actor = filtersArg.actor;
            if (filtersArg.action) params.action = filtersArg.action;
            const [logs, sum] = await Promise.all([
                api.get("/audit/logs", { params }),
                api.get("/audit/logs/summary"),
            ]);
            setEntries(logs.data.entries || []);
            setSummary(sum.data || {});
        } catch (e) {
            toast.error("Failed to load audit trail");
        } finally { setLoading(false); }
    };
    useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

    const applyFilter = () => load(filters);
    const clearFilters = () => {
        const empty = { entity: "", actor: "", action: "" };
        setFilters(empty);
        load(empty);
    };

    const fmtTime = (iso) => {
        try { return new Date(iso).toLocaleString("en-IN", { hour12: false }); }
        catch { return iso; }
    };

    const shortJson = (v) => {
        if (v === null || v === undefined) return "—";
        if (typeof v === "string") return v.length > 60 ? v.slice(0, 60) + "…" : v;
        try {
            const s = JSON.stringify(v);
            return s.length > 60 ? s.slice(0, 60) + "…" : s;
        } catch { return String(v); }
    };

    return (
        <div className="space-y-3">
            <Panel title="Audit Trail Summary" subtitle="Row-level tracking · Who edited what, when">
                {summary && (
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                        <div className="border border-panel bg-[var(--bg-base)] p-3">
                            <div className="text-[10px] font-mono-fin uppercase tracking-wider text-[var(--text-secondary)] mb-1">Total Events</div>
                            <div className="font-mono-fin text-2xl text-[var(--brand-amber)]">{summary.total ?? 0}</div>
                        </div>
                        <div className="border border-panel bg-[var(--bg-base)] p-3">
                            <div className="text-[10px] font-mono-fin uppercase tracking-wider text-[var(--text-secondary)] mb-1">Top Actor</div>
                            <div className="font-mono-fin text-sm text-[var(--text-primary)] truncate" title={summary.by_actor?.[0]?.actor}>
                                {summary.by_actor?.[0]?.actor || "—"}
                            </div>
                            <div className="font-mono-fin text-[10px] text-[var(--text-muted)]">{summary.by_actor?.[0]?.count ?? 0} events</div>
                        </div>
                        <div className="border border-panel bg-[var(--bg-base)] p-3">
                            <div className="text-[10px] font-mono-fin uppercase tracking-wider text-[var(--text-secondary)] mb-1">Top Entity</div>
                            <div className="font-mono-fin text-sm text-[var(--text-primary)] truncate">{summary.by_entity?.[0]?.entity || "—"}</div>
                            <div className="font-mono-fin text-[10px] text-[var(--text-muted)]">{summary.by_entity?.[0]?.count ?? 0} events</div>
                        </div>
                        <div className="border border-panel bg-[var(--bg-base)] p-3">
                            <div className="text-[10px] font-mono-fin uppercase tracking-wider text-[var(--text-secondary)] mb-1">Top Action</div>
                            <div className="font-mono-fin text-sm text-[var(--text-primary)] truncate">{summary.by_action?.[0]?.action || "—"}</div>
                            <div className="font-mono-fin text-[10px] text-[var(--text-muted)]">{summary.by_action?.[0]?.count ?? 0} events</div>
                        </div>
                    </div>
                )}
            </Panel>

            <Panel title="Event Log" subtitle="Newest first · Click a row to inspect before/after payloads">
                <div className="flex flex-wrap gap-2 mb-3 items-end">
                    <div>
                        <div className="text-[10px] font-mono-fin uppercase tracking-wider text-[var(--text-secondary)] mb-1">Entity</div>
                        <input
                            data-testid="audit-filter-entity"
                            value={filters.entity}
                            onChange={(e) => setFilters({ ...filters, entity: e.target.value })}
                            placeholder="e.g. projects, team_user"
                            className="bg-[var(--bg-base)] border border-panel px-3 py-1.5 text-xs font-mono-fin w-44 focus:border-[var(--brand-amber)] focus:outline-none"
                        />
                    </div>
                    <div>
                        <div className="text-[10px] font-mono-fin uppercase tracking-wider text-[var(--text-secondary)] mb-1">Actor</div>
                        <input
                            data-testid="audit-filter-actor"
                            value={filters.actor}
                            onChange={(e) => setFilters({ ...filters, actor: e.target.value })}
                            placeholder="email"
                            className="bg-[var(--bg-base)] border border-panel px-3 py-1.5 text-xs font-mono-fin w-56 focus:border-[var(--brand-amber)] focus:outline-none"
                        />
                    </div>
                    <div>
                        <div className="text-[10px] font-mono-fin uppercase tracking-wider text-[var(--text-secondary)] mb-1">Action</div>
                        <input
                            data-testid="audit-filter-action"
                            value={filters.action}
                            onChange={(e) => setFilters({ ...filters, action: e.target.value })}
                            placeholder="create / update / delete / …"
                            className="bg-[var(--bg-base)] border border-panel px-3 py-1.5 text-xs font-mono-fin w-44 focus:border-[var(--brand-amber)] focus:outline-none"
                        />
                    </div>
                    <button
                        data-testid="audit-apply"
                        onClick={applyFilter}
                        className="bg-[var(--brand-amber)] text-black px-3 py-1.5 text-[11px] font-mono-fin uppercase tracking-wider hover:bg-[var(--bg-panel-hover)] transition-colors flex items-center gap-1"
                    >
                        <Filter size={12} /> Apply
                    </button>
                    <button
                        data-testid="audit-clear"
                        onClick={clearFilters}
                        className="border border-panel px-3 py-1.5 text-[11px] font-mono-fin uppercase tracking-wider hover:border-[var(--brand-amber)]/40 transition-colors"
                    >
                        Clear
                    </button>
                    <button
                        data-testid="audit-refresh"
                        onClick={load}
                        className="border border-panel px-3 py-1.5 text-[11px] font-mono-fin uppercase tracking-wider hover:border-[var(--brand-amber)] hover:text-[var(--brand-amber)] transition-colors flex items-center gap-1"
                    >
                        <RefreshCw size={12} /> Refresh
                    </button>
                </div>

                {loading ? (
                    <div className="text-xs font-mono-fin text-[var(--text-secondary)] p-4">Loading audit trail…</div>
                ) : entries.length === 0 ? (
                    <div className="text-xs font-mono-fin text-[var(--text-secondary)] p-4">No audit events recorded yet. Try editing a record in Data Manager, Team, or Settings and refresh.</div>
                ) : (
                    <div className="overflow-x-auto">
                        <table className="w-full text-xs font-mono-fin" data-testid="audit-table">
                            <thead>
                                <tr className="text-left text-[10px] uppercase tracking-wider text-[var(--text-secondary)] border-b border-panel">
                                    <th className="py-2 pr-3">Time (IST)</th>
                                    <th className="py-2 pr-3">Actor</th>
                                    <th className="py-2 pr-3">Action</th>
                                    <th className="py-2 pr-3">Entity</th>
                                    <th className="py-2 pr-3">Item</th>
                                    <th className="py-2 pr-3">Before</th>
                                    <th className="py-2 pr-3">After</th>
                                    <th className="py-2 pr-3">Note</th>
                                </tr>
                            </thead>
                            <tbody>
                                {entries.map((e, i) => {
                                    const isOpen = expanded === e.id;
                                    return (
                                        <React.Fragment key={e.id || i}>
                                            <tr
                                                data-testid={`audit-row-${i}`}
                                                onClick={() => setExpanded(isOpen ? null : e.id)}
                                                className="border-b border-panel/50 hover:bg-[var(--bg-panel-hover)]/[0.03] cursor-pointer"
                                            >
                                                <td className="py-1.5 pr-3 text-[var(--text-primary)]/85 whitespace-nowrap">{fmtTime(e.timestamp)}</td>
                                                <td className="py-1.5 pr-3 text-[var(--brand-amber)]">{e.actor}</td>
                                                <td className="py-1.5 pr-3 text-[var(--pos)] uppercase">{e.action}</td>
                                                <td className="py-1.5 pr-3 text-[var(--text-primary)]">{e.entity}</td>
                                                <td className="py-1.5 pr-3 text-[var(--text-primary)]/70 max-w-[140px] truncate" title={e.item_id}>{e.item_id || "—"}</td>
                                                <td className="py-1.5 pr-3 text-[var(--text-primary)]/60 max-w-[220px] truncate" title={typeof e.before === "string" ? e.before : JSON.stringify(e.before || "")}>{shortJson(e.before)}</td>
                                                <td className="py-1.5 pr-3 text-[var(--text-primary)]/85 max-w-[220px] truncate" title={typeof e.after === "string" ? e.after : JSON.stringify(e.after || "")}>{shortJson(e.after)}</td>
                                                <td className="py-1.5 pr-3 text-[var(--text-primary)]/70 max-w-[220px] truncate" title={e.note || ""}>{e.note || "—"}</td>
                                            </tr>
                                            {isOpen && (
                                                <tr className="bg-[var(--bg-base)]/60 border-b border-panel/50">
                                                    <td colSpan={8} className="p-3">
                                                        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                                                            <div>
                                                                <div className="text-[10px] uppercase tracking-wider text-[var(--neg)] mb-1">Before</div>
                                                                <pre className="text-[11px] font-mono-fin text-[var(--text-primary)]/85 bg-[var(--neg)]/5 border border-[var(--neg)]/20 p-2 overflow-x-auto whitespace-pre-wrap break-all max-h-64">{e.before ? JSON.stringify(e.before, null, 2) : "—"}</pre>
                                                            </div>
                                                            <div>
                                                                <div className="text-[10px] uppercase tracking-wider text-[var(--pos)] mb-1">After</div>
                                                                <pre className="text-[11px] font-mono-fin text-[var(--text-primary)]/85 bg-[var(--pos)]/5 border border-[var(--pos)]/20 p-2 overflow-x-auto whitespace-pre-wrap break-all max-h-64">{e.after ? JSON.stringify(e.after, null, 2) : "—"}</pre>
                                                            </div>
                                                        </div>
                                                        {e.note && (
                                                            <div className="mt-2 text-[11px] text-[var(--text-primary)]/80"><span className="text-[var(--text-secondary)] uppercase text-[10px] tracking-wider mr-2">Note:</span>{e.note}</div>
                                                        )}
                                                    </td>
                                                </tr>
                                            )}
                                        </React.Fragment>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                )}
                <div className="mt-2 text-[10px] font-mono-fin text-[var(--text-muted)] uppercase tracking-wider">
                    Showing {entries.length} event(s) · Backed by MongoDB collection <span className="text-[var(--text-primary)]/70">cfo_audit_log</span>
                </div>
            </Panel>
        </div>
    );
}


// ============ Wipe All Demo Data Panel ============
function WipeAllDataPanel() {
    const [confirmPhrase, setConfirmPhrase] = useState("");
    const [keepUsers, setKeepUsers] = useState(true);
    const [keepCompany, setKeepCompany] = useState(true);
    const [busy, setBusy] = useState(false);
    const [result, setResult] = useState(null);

    const wipe = async () => {
        if (confirmPhrase !== "DELETE ALL DATA") {
            toast.error("Type DELETE ALL DATA exactly to enable the button");
            return;
        }
        if (!window.confirm("This will delete EVERY project, vendor, client, invoice, bank txn, GRN, PO, quotation, sales order, payroll run, imported statement and audit-log-visible data. Are you absolutely sure?")) {
            return;
        }
        setBusy(true); setResult(null);
        try {
            const r = await api.post("/settings/wipe-all-data", {
                confirm_phrase: confirmPhrase,
                keep_users: keepUsers,
                keep_company_profile: keepCompany,
            });
            setResult(r.data);
            toast.success(`Wiped ${r.data.total_rows_removed} rows across ${Object.keys(r.data.wiped).length} areas`);
            setConfirmPhrase("");
            setTimeout(() => window.location.reload(), 2500);
        } catch (e) {
            toast.error("Wipe failed: " + (e.response?.data?.detail || e.message));
        } finally { setBusy(false); }
    };

    return (
        <Panel title="🔥 DELETE ALL DEMO DATA" subtitle="Nukes every record across all 14 entities + live vouchers/POs/GRNs/bank imports — cannot be undone">
            <div className="space-y-4">
                <div className="border border-[var(--neg)] bg-[var(--neg)]/10 p-4 space-y-2">
                    <div className="flex items-start gap-2 text-[var(--neg)]">
                        <AlertTriangle size={16} className="mt-0.5 flex-shrink-0" />
                        <div className="text-xs font-mono-fin">
                            <div className="font-bold uppercase tracking-wider mb-1">Danger Zone</div>
                            <div className="text-[var(--text-primary)]/85">
                                This is different from Fresh Start — Fresh Start also runs the FY reset. Wipe All Data leaves you with a completely empty app so you can feed your own real data from scratch (via the Import Hub or Data Manager).
                            </div>
                        </div>
                    </div>
                </div>

                <div className="space-y-2">
                    <label className="flex items-center gap-2 text-xs font-body text-[var(--text-primary)]/85">
                        <input
                            type="checkbox"
                            data-testid="wipe-keep-users"
                            checked={keepUsers}
                            onChange={(e) => setKeepUsers(e.target.checked)}
                            className="accent-[var(--brand-amber)]"
                        />
                        Preserve team logins (recommended — else only MD & CFO survive)
                    </label>
                    <label className="flex items-center gap-2 text-xs font-body text-[var(--text-primary)]/85">
                        <input
                            type="checkbox"
                            data-testid="wipe-keep-company"
                            checked={keepCompany}
                            onChange={(e) => setKeepCompany(e.target.checked)}
                            className="accent-[var(--brand-amber)]"
                        />
                        Preserve company profile (name, GSTIN, PAN, address)
                    </label>
                </div>

                <div>
                    <div className="text-[10px] font-mono-fin uppercase tracking-wider text-[var(--text-secondary)] mb-1">
                        Type <span className="text-[var(--neg)] font-bold">DELETE ALL DATA</span> to unlock the button
                    </div>
                    <input
                        type="text"
                        data-testid="wipe-confirm-input"
                        value={confirmPhrase}
                        onChange={(e) => setConfirmPhrase(e.target.value)}
                        placeholder="DELETE ALL DATA"
                        className="w-full bg-[var(--bg-base)] border border-[var(--neg)]/50 focus:border-[var(--neg)] focus:outline-none px-3 py-2 text-xs font-mono-fin"
                    />
                </div>

                <button
                    data-testid="wipe-all-btn"
                    onClick={wipe}
                    disabled={busy || confirmPhrase !== "DELETE ALL DATA"}
                    className="bg-[var(--neg)] text-[var(--text-primary)] px-4 py-2 text-xs font-mono-fin uppercase tracking-wider hover:bg-red-800 transition-colors disabled:opacity-30 disabled:cursor-not-allowed flex items-center gap-2"
                >
                    <X size={12} /> {busy ? "Deleting…" : "Delete ALL Demo Data Now"}
                </button>

                {result && (
                    <div className="border border-[var(--pos)]/40 bg-[var(--pos)]/5 p-3">
                        <div className="text-[10px] font-mono-fin uppercase tracking-wider text-[var(--pos)] mb-2 flex items-center gap-1">
                            <Check size={12} /> Wipe Complete — {result.total_rows_removed} rows removed · reloading…
                        </div>
                        <div className="text-[10px] font-mono-fin text-[var(--text-primary)]/70 grid grid-cols-2 gap-x-4 gap-y-1 max-h-40 overflow-y-auto">
                            {Object.entries(result.wiped).map(([k, v]) => (
                                <div key={k} className="flex justify-between border-b border-panel/40 py-0.5">
                                    <span className="truncate">{k}</span>
                                    <span className="text-[var(--brand-amber)] ml-2">{v}</span>
                                </div>
                            ))}
                        </div>
                    </div>
                )}
            </div>
        </Panel>
    );
}


// ============ Appearance Panel · Theme toggle ============
function AppearancePanel() {
    const { theme, setTheme } = useTheme();
    return (
        <Panel title="Appearance" subtitle="Switch between boardroom (light) and late-night ops (dark) — persists across sessions">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <button
                    data-testid="theme-light-btn"
                    onClick={() => setTheme("light")}
                    className={`p-5 text-left border rounded transition-all ${
                        theme === "light"
                            ? "border-[var(--pbi-blue)] ring-2 ring-[var(--pbi-blue-dim)] bg-[var(--pbi-blue-dim)]"
                            : "border-panel hover:border-[var(--pbi-blue)]"
                    }`}
                >
                    <div className="flex items-center justify-between mb-3">
                        <Sun size={22} className="text-[var(--brand-amber)]" />
                        {theme === "light" && <Check size={18} className="text-[var(--pbi-blue)]" />}
                    </div>
                    <div className="font-semibold text-[var(--text-primary)] text-base mb-1">Light · Boardroom</div>
                    <div className="text-xs text-[var(--text-secondary)]">
                        Off-white canvas · Segoe UI · print-ready. Ideal for board decks, morning reviews and daylight work.
                    </div>
                </button>

                <button
                    data-testid="theme-dark-btn"
                    onClick={() => setTheme("dark")}
                    className={`p-5 text-left border rounded transition-all ${
                        theme === "dark"
                            ? "border-[var(--pbi-blue)] ring-2 ring-[var(--pbi-blue-dim)] bg-[var(--pbi-blue-dim)]"
                            : "border-panel hover:border-[var(--pbi-blue)]"
                    }`}
                >
                    <div className="flex items-center justify-between mb-3">
                        <Moon size={22} className="text-[var(--brand-amber)]" />
                        {theme === "dark" && <Check size={18} className="text-[var(--pbi-blue)]" />}
                    </div>
                    <div className="font-semibold text-[var(--text-primary)] text-base mb-1">Dark · Late-Night Ops</div>
                    <div className="text-xs text-[var(--text-secondary)]">
                        Charcoal terminal · easier on eyes for long ops sessions after sundown or in low-light command centers.
                    </div>
                </button>
            </div>
            <div className="mt-4 text-[11px] text-[var(--text-muted)] border-t border-panel pt-3">
                Amber accents (Power BI signature yellow) remain identical across both themes — every KPI tile, ticker highlight and slicer selection keeps the same amber halo.
            </div>
        </Panel>
    );
}


// ============ Main Page ============
export default function Settings() {
    const [tab, setTab] = useState("company");
    const [freshOpen, setFreshOpen] = useState(false);

    const tabs = [
        { key: "company", label: "Company Profile", icon: Building2 },
        { key: "appearance", label: "Appearance", icon: Palette },
        { key: "fresh", label: "Fresh Start", icon: Sparkles },
        { key: "excel", label: "Import Hub", icon: FileSpreadsheet },
        { key: "audit", label: "Audit Trail", icon: ShieldCheck },
    ];

    return (
        <div className="space-y-4">
            <div>
                <div className="font-mono-fin text-[10px] uppercase tracking-[0.25em] text-[var(--brand-amber)] mb-1">// SETTINGS</div>
                <h1 className="font-display font-black text-3xl flex items-center gap-3">
                    <SettingsIcon className="text-[var(--brand-amber)]" size={26} />
                    Company & System Settings
                </h1>
                <p className="mt-1 font-mono-fin text-[11px] text-[var(--text-secondary)] uppercase tracking-wider">
                    Company profile · Fresh start · Bulk Excel import · Financial year control
                </p>
            </div>

            <div className="flex gap-1 border border-panel bg-panel p-1 w-fit" data-testid="settings-tabs">
                {tabs.map((t) => {
                    const Icon = t.icon;
                    const active = tab === t.key;
                    return (
                        <button
                            key={t.key}
                            data-testid={`settings-tab-${t.key}`}
                            onClick={() => setTab(t.key)}
                            className={`flex items-center gap-2 px-3 py-1.5 text-[11px] font-mono-fin uppercase tracking-wider transition-colors ${active ? "bg-[var(--brand-amber)] text-black" : "text-[var(--text-secondary)] hover:text-[var(--text-primary)]"}`}
                        >
                            <Icon size={12} /> {t.label}
                        </button>
                    );
                })}
            </div>

            {tab === "company" && <CompanyPanel />}
            {tab === "appearance" && <AppearancePanel />}
            {tab === "fresh" && (
                <div className="space-y-3">
                    <Panel title="Fresh Start Wizard" subtitle="Wipe demo data · Begin FY 2026-27 · Keep team logins">
                        <div className="text-sm font-body space-y-3">
                            <p className="text-[var(--text-primary)]/85">
                                Ready to move from hypothetical demo data to your real company records? Fresh Start clears all operational data
                                and repositions the fiscal calendar to <span className="text-[var(--brand-amber)] font-display font-bold">FY 2026-27 · 1 Apr 2026 → 31 Mar 2027</span>.
                            </p>
                            <div className="grid grid-cols-1 md:grid-cols-3 gap-2 text-xs">
                                <div className="border border-[var(--neg)]/30 bg-[var(--neg)]/5 p-3">
                                    <div className="font-mono-fin uppercase tracking-wider text-[var(--neg)] text-[10px] mb-1">Wiped</div>
                                    <ul className="text-[var(--text-primary)]/85 space-y-0.5 list-disc list-inside">
                                        <li>All 14 data entities</li>
                                        <li>All vouchers &amp; ledgers</li>
                                        <li>P&amp;L / TB history</li>
                                        <li>Monthly series &amp; forecast</li>
                                    </ul>
                                </div>
                                <div className="border border-[var(--pos)]/30 bg-[var(--pos)]/5 p-3">
                                    <div className="font-mono-fin uppercase tracking-wider text-[var(--pos)] text-[10px] mb-1">Preserved</div>
                                    <ul className="text-[var(--text-primary)]/85 space-y-0.5 list-disc list-inside">
                                        <li>Team &amp; login accounts</li>
                                        <li>Company profile (address, GSTIN)</li>
                                        <li>Statutory compliance calendar</li>
                                        <li>Chart of accounts</li>
                                    </ul>
                                </div>
                                <div className="border border-[var(--brand-amber)]/30 bg-[var(--brand-amber-dim)] p-3">
                                    <div className="font-mono-fin uppercase tracking-wider text-[var(--brand-amber)] text-[10px] mb-1">Then</div>
                                    <ul className="text-[var(--text-primary)]/85 space-y-0.5 list-disc list-inside">
                                        <li>Import via Excel template</li>
                                        <li>Or enter one voucher at a time</li>
                                        <li>Agents recompute from your data</li>
                                    </ul>
                                </div>
                            </div>
                            <button
                                data-testid="open-fresh-start"
                                onClick={() => setFreshOpen(true)}
                                className="bg-[var(--neg)] text-[var(--text-primary)] px-4 py-2 text-xs font-mono-fin uppercase tracking-wider hover:bg-red-700 transition-colors flex items-center gap-2"
                            >
                                <RotateCcw size={12} /> Open Fresh Start Wizard
                            </button>
                        </div>
                    </Panel>

                    <WipeAllDataPanel />
                </div>
            )}
            {tab === "excel" && <ImportHubPanel />}
            {tab === "audit" && <AuditTrailPanel />}

            <FreshStartModal open={freshOpen} onClose={() => setFreshOpen(false)} onDone={() => window.location.reload()} />
        </div>
    );
}
